import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouteLink, useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import styled from 'styled-components/macro';
import { Formik } from 'formik';
import { string, object, boolean } from 'yup';
import TagManager from 'react-gtm-module';
import Typography from '../ui/v2/Typography';
import Icon from '../ui/Icon';
import AlertToast from '../ui/AlertToast';
import InputCheckBox from '../ui/v2/InputCheckBox';
import Input from '../ui/v2/Input';
import InputPassword from '../ui/v2/InputPassword';
import Button from '../ui/v2/Button';
import FacebookButton from './FacebookButton';
import { AuthContext } from '../../context/AuthContext';

import { getLocaleFromUrl, getLead, hasNewsletterCheckBox } from '../../utils';

interface RegisterFormProps {
  dialog?: boolean;
  registerUser(data: any): any;
  loginUser(data: any): any;
  loginFacebook(data: any, newsletter: boolean): any;
  setAuthView?(view: '#login' | '#register'): void;
  handleCloseDialog?(): void;
  setShowPromoModal?: any;
  showPromoModal?: boolean;
}

const Link = styled(RouteLink)`
  display: inline;
  text-decoration: underline !important;
  margin: 0 4px;
  cursor: pointer;
`;

const RegisterForm = ({
  dialog,
  registerUser,
  loginUser,
  loginFacebook,
  setAuthView,
  handleCloseDialog,
  setShowPromoModal,
  showPromoModal,
}: RegisterFormProps) => {
  const { i18n, t } = useTranslation();
  let history = useHistory();

  const { setAuthData } = useContext(AuthContext);

  const theme = useTheme();
  const xsDown = useMediaQuery(theme.breakpoints.down('xs'));

  const [leadEmail, setLeadEmail] = useState('');
  const [extraErrorMessage, setExtraErrorMessage] = useState('');
  const [isNewsletter, setNewsletter] = useState<boolean>(true);

  useEffect(() => {
    i18n.changeLanguage(getLocaleFromUrl());
  }, [i18n]);

  useEffect(() => {
    const getLeadEmail = async (leadId) => {
      if (parseInt(leadId)) {
        const response = await getLead(leadId);
        if (response.email) {
          setLeadEmail(response.email);
        }
      }
    };

    getLeadEmail(localStorage.getItem('lead_id'));
  }, []);

  const handleFacebook = (e) => {
    loginFacebook(e, isNewsletter);
  };

  return (
    <>
      <Container maxWidth={false}>
        <Grid
          container
          alignItems="center"
          justify="center"
          alignContent="center"
        >
          <Hidden xsDown={!dialog}>
            <Grid
              container
              alignItems="center"
              style={{ marginBottom: xsDown ? '0' : '12px' }}
            >
              <Grid item sm={dialog ? 6 : 4}>
                <Typography variant={'h2'}>{t('Create an account')}</Typography>
              </Grid>
              <Grid item sm={dialog ? 6 : 8}>
                <Typography>
                  {t('Already have an account?')}
                  <Link
                    to={setAuthView ? '#' : t('/login')}
                    onClick={() => setAuthView && setAuthView('#login')}
                  >
                    {t('Log in')}
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Hidden>
          <Grid item xs={12} sm={dialog ? 11 : 6} lg={dialog ? 11 : 4}>
            {extraErrorMessage && (
              <AlertToast
                type={'error'}
                showErrorToast={!!extraErrorMessage}
                hide={() => setExtraErrorMessage('')}
                message={extraErrorMessage}
              />
            )}
            <Formik
              initialValues={{
                name: '',
                email: leadEmail || '',
                pass: '',
                terms: true,
              }}
              validationSchema={object().shape({
                email: string()
                  .email(t('Must be a valid email'))
                  .required(t('This field can not be empty')),
                pass: string()
                  .min(5, t('Password must have a minimum of 5 characters'))
                  .required(t('This field can not be empty')),
                name: string().required(t('This field can not be empty')),
                terms: boolean().oneOf([true]),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  setSubmitting(true);

                  const response = await registerUser({
                    nombre: values.name,
                    email: values.email,
                    pass: values.pass,
                    terms: values.terms,
                    newsletter: isNewsletter,
                  });

                  if (response.data.error) throw response;

                  if (response.data?.hasOwnProperty('customerId')) {
                    localStorage.setItem('user_id', response.data.customerId);
                  }

                  if (response.data?.hasOwnProperty('leadId')) {
                    localStorage.setItem('lead_id', response.data.leadId);
                  }

                  const enter_url = sessionStorage.getItem('enter_url');
                  const referrer_url = sessionStorage.getItem('referrer_url');
                  const utm_campaign = sessionStorage.getItem('utm_campaign');
                  const utm_medium = sessionStorage.getItem('utm_medium');
                  const utm_source = sessionStorage.getItem('utm_source');
                  const loginResponse = await loginUser({
                    email: values.email,
                    pass: values.pass,
                    enter_url: enter_url ? enter_url.replace(/&/g, '%26') : '',
                    referrer_url: referrer_url
                      ? referrer_url.replace(/&/g, '%26')
                      : '',
                    utm_campaign: utm_campaign ? utm_campaign : '',
                    utm_medium: utm_medium ? utm_medium : '',
                    utm_source: utm_source ? utm_source : '',
                  });

                  if (loginResponse.data.error) throw response;

                  setAuthData({
                    isLoggedIn: true,
                    authToken: loginResponse.data.token,
                  });

                  TagManager.dataLayer({
                    dataLayer: { event: 'userLogin_ok' },
                  });

                  if (window.location.hash !== '#promo-register') {
                    if (handleCloseDialog) {
                      handleCloseDialog();
                    } else {
                      history.push(t('/my-account'));
                    }
                  }

                  if (showPromoModal) {
                    if (handleCloseDialog) {
                      handleCloseDialog();
                    }
                    setShowPromoModal(!showPromoModal);
                  }
                } catch (error) {
                  let message;
                  switch (error?.data?.extraInfo?.errorCode) {
                    case 'ERR_CUS_CREATE_01':
                      message = t('Wrong email');
                      break;
                    case 'ERR_CUS_CREATE_02':
                      message = t('Wrong password');
                      break;
                    case 'ERR_CUS_CREATE_05':
                      message = t('Email already exists');
                      break;
                    default:
                      message = t('Error');
                  }
                  setExtraErrorMessage(message);
                  setSubmitting(false);
                }
              }}
              render={({
                errors,
                touched,
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                isSubmitting,
              }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <Grid item xs={12}>
                      <Input
                        labelText={t('Name')}
                        inputName={'name'}
                        type={'name'}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        width={'100%'}
                        placeHolderText={t('Name')}
                        error={touched.name && errors.name ? true : false}
                        errorText={errors.name ? errors.name : ''}
                        autoComplete="name"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Input
                        labelText={t('Email')}
                        inputName={'email'}
                        type={'email'}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        width={'100%'}
                        placeHolderText={t('Email')}
                        error={touched.email && errors.email ? true : false}
                        errorText={errors.email ? errors.email : ''}
                        autoComplete="username"
                      />
                      <InputPassword
                        labelText={t('Password')}
                        inputName={'pass'}
                        type={'password'}
                        value={values.pass}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        width={'100%'}
                        placeHolderText={t('Password')}
                        error={touched.pass && errors.pass ? true : false}
                        errorText={errors.pass ? errors.pass : ''}
                        autoComplete="current-password"
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      style={{ margin: xsDown ? '8px 0' : '16px 0' }}
                    >
                      {hasNewsletterCheckBox() && (
                        <InputCheckBox
                          name={'newsletter'}
                          checked={isNewsletter}
                          onChange={() => {
                            setNewsletter(!isNewsletter);
                          }}
                          label={t(
                            'I  agree to receive exclusive Exoticca promotions by email.',
                          )}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} style={{ margin: 'auto' }}>
                      <Button type="submit" loading={isSubmitting}>
                        <span>{t('Create an account')}</span>
                        <Icon
                          icon={'iconFSArrow'}
                          size={17}
                          customStyle={{
                            marginLeft: '12px',
                            marginBottom: '1px',
                          }}
                        />
                      </Button>
                      <Typography variant={'smallText'}>
                        {t('By creating an account, you agree to the')}
                        <Link to={t('/privacy-policy')} target={'_blank'}>
                          {t('privacy policy')}
                        </Link>
                        {t('and the')}
                        <Link to={t('/cookies-policy')} target={'_blank'}>
                          {t('cookie policy')}
                        </Link>
                      </Typography>
                    </Grid>
                  </form>
                );
              }}
            />
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth={false} style={{ padding: 0 }}>
        <Grid
          item
          xs={12}
          sm={12}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: xsDown ? '8px 0' : '24px 0',
            margin: xsDown ? '8px 0 0' : '24px 0 0',
            backgroundColor: '#f2f2f2',
          }}
        >
          <Hidden xsDown>
            <Typography
              variant={'bodyText'}
              customStyle={{
                display: 'inline-block',
                margin: '8px 16px',
              }}
            >
              {t('Sign in with Facebook')}
            </Typography>
          </Hidden>
          <FacebookButton authCallback={handleFacebook} />
        </Grid>
      </Container>
    </>
  );
};

export default RegisterForm;
